import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Popover,
  useMediaQuery,
  useTheme,
  Collapse,
  Divider,
  Grid,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { toast, ToastContainer } from "react-toastify";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import "react-toastify/dist/ReactToastify.css";
import {
  DivisionCreate,
  DivisionList,
  DivisionDelete,
  DivisionUpdate,
} from "../../../Services/Services";

const DivisionManagementTable = () => {
  const [divisions, setDivisions] = useState([]);
  const [filter, setFilter] = useState("");
  const [sortAsc] = useState(true);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [divisionToDelete, setDivisionToDelete] = useState(null);
  const [errors, setErrors] = useState({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedDivision, setExpandedDivision] = useState(null);

  // Form state for Add Modal
  const [newDivisionName, setNewDivisionName] = useState("");
  const [newCompanyName, setNewCompanyName] = useState("");
  const [newGstNo, setNewGstNo] = useState("");
  const [newPanNo, setNewPanNo] = useState("");
  const [newAddress, setNewAddress] = useState("");

  // Form state for Edit Modal
  const [editDivisionName, setEditDivisionName] = useState("");
  const [editCompanyName, setEditCompanyName] = useState("");
  const [editGstNo, setEditGstNo] = useState("");
  const [editPanNo, setEditPanNo] = useState("");
  const [editAddress, setEditAddress] = useState("");

  useEffect(() => {
    refreshDivisions();
  }, []);

  function formatOnlyDate(dateTimeString) {
    if (!dateTimeString) return "N/A";
    const datePart = dateTimeString.split("T")[0];
    const [year, month, day] = datePart.split("-");
    return `${day}-${month}-${year}`;
  }

  const refreshDivisions = async () => {
    try {
      const res = await DivisionList();
      const list = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.results)
            ? res.results
            : [];
      setDivisions(list);
    } catch (error) {
      console.error("Error fetching divisions:", error);
      toast.error("Unable to load divisions");
    }
  };

  // Reset Add Form
  const resetAddForm = () => {
    setNewDivisionName("");
    setNewCompanyName("");
    setNewGstNo("");
    setNewPanNo("");
    setNewAddress("");
    setErrors({});
  };

  // Reset Edit Form
  const resetEditForm = () => {
    setEditDivisionName("");
    setEditCompanyName("");
    setEditGstNo("");
    setEditPanNo("");
    setEditAddress("");
    setEditingIndex(null);
    setErrors({});
  };

  // GST Validation
  const validateGstNo = (value) => {
    if (!value || value.trim() === "") return true; // Optional field
    const gstPattern = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstPattern.test(value.toUpperCase());
  };

  // PAN Validation
  const validatePanNo = (value) => {
    if (!value || value.trim() === "") return true; // Optional field
    const panPattern = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    return panPattern.test(value.toUpperCase());
  };

  const handleEdit = (division) => {
    const editRow = (Array.isArray(divisions) ? divisions : []).find(
      (d) => d.division_id === division.division_id
    );
    if (editRow) {
      setErrors({});
      setEditDivisionName(editRow.division || "");
      setEditCompanyName(editRow.company_name || "");
      setEditGstNo(editRow.gst_no || "");
      setEditPanNo(editRow.pan_no || "");
      setEditAddress(editRow.address || "");
      setEditingIndex(editRow);
      setOpenEditModal(true);
    }
  };

  const handleUpdateDivision = async () => {
    const trimmedName = editDivisionName.trim();
    const trimmedCompany = editCompanyName.trim();
    const trimmedGst = editGstNo.trim().toUpperCase();
    const trimmedPan = editPanNo.trim().toUpperCase();
    const trimmedAddress = editAddress.trim();

    // Validation
    const newErrors = {};

    if (trimmedName === "") {
      newErrors.division = "Division name is required.";
    }

    if (trimmedCompany === "") {
      newErrors.company_name = "Company name is required.";
    }

    if (trimmedGst && !validateGstNo(trimmedGst)) {
      newErrors.gst_no = "Invalid GST format. Example: 22AAAAA0000A1Z5";
    }

    if (trimmedPan && !validatePanNo(trimmedPan)) {
      newErrors.pan_no = "Invalid PAN format. Example: ABCDE1234F";
    }

    if (trimmedAddress && trimmedAddress.length < 5) {
      newErrors.address = "Address must be at least 5 characters.";
    }

    // Cross validation: GST and PAN matching
    if (trimmedGst && trimmedPan) {
      const panFromGst = trimmedGst.substring(2, 12);
      if (panFromGst !== trimmedPan) {
        newErrors.pan_no = "PAN Number does not match with GST Number.";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Client-side duplicate check (excluding current row)
    const exists = (Array.isArray(divisions) ? divisions : []).some(
      (d) =>
        d.division_id !== editingIndex.division_id &&
        d.division?.trim().toLowerCase() === trimmedName.toLowerCase() &&
        d.company_name?.trim().toLowerCase() === trimmedCompany.toLowerCase()
    );
    if (exists) {
      const msg = "Division with this company name already exists";
      toast.info(msg);
      setErrors({ division: msg });
      return;
    }

    setErrors({});
    try {
      await DivisionUpdate(editingIndex.division_id, {
        division: trimmedName,
        company_name: trimmedCompany,
        gst_no: trimmedGst || null,
        pan_no: trimmedPan || null,
        address: trimmedAddress || null,
      });
      toast.success("Division updated successfully!");
      setOpenEditModal(false);
      resetEditForm();
      await refreshDivisions();
    } catch (error) {
      const serverErrors = error?.response?.data;
      if (serverErrors) {
        const newErrors = {};
        if (serverErrors.division) {
          newErrors.division = serverErrors.division[0] || serverErrors.division;
          toast.info(newErrors.division);
        }
        if (serverErrors.company_name) {
          newErrors.company_name = serverErrors.company_name[0] || serverErrors.company_name;
        }
        if (serverErrors.gst_no) {
          newErrors.gst_no = serverErrors.gst_no[0] || serverErrors.gst_no;
        }
        if (serverErrors.pan_no) {
          newErrors.pan_no = serverErrors.pan_no[0] || serverErrors.pan_no;
        }
        if (serverErrors.address) {
          newErrors.address = serverErrors.address[0] || serverErrors.address;
        }
        setErrors(newErrors);
      } else {
        toast.error("Failed to update division");
      }
    }
  };

  const getFilteredSortedDivisions = () => {
    const list = Array.isArray(divisions) ? divisions : [];
    let filtered = list.filter((division) =>
      division?.division?.toLowerCase().includes(filter.toLowerCase()) ||
      division?.company_name?.toLowerCase().includes(filter.toLowerCase()) ||
      division?.gst_no?.toLowerCase().includes(filter.toLowerCase()) ||
      division?.pan_no?.toLowerCase().includes(filter.toLowerCase()) ||
      division?.address?.toLowerCase().includes(filter.toLowerCase())
    );
    filtered.sort((a, b) =>
      sortAsc ? a.division.localeCompare(b.division) : b.division.localeCompare(a.division)
    );
    return filtered;
  };

  const handleAddNewDivision = async () => {
    const trimmedDivision = newDivisionName.trim();
    const trimmedCompany = newCompanyName.trim();
    const trimmedGst = newGstNo.trim().toUpperCase();
    const trimmedPan = newPanNo.trim().toUpperCase();
    const trimmedAddress = newAddress.trim();

    // Validation
    const newErrors = {};

    if (trimmedDivision === "") {
      newErrors.division = "Division name is required";
    }

    if (trimmedCompany === "") {
      newErrors.company_name = "Company name is required";
    }

    if (trimmedGst && !validateGstNo(trimmedGst)) {
      newErrors.gst_no = "Invalid GST format. Example: 22AAAAA0000A1Z5";
    }

    if (trimmedPan && !validatePanNo(trimmedPan)) {
      newErrors.pan_no = "Invalid PAN format. Example: ABCDE1234F";
    }

    if (trimmedAddress && trimmedAddress.length < 5) {
      newErrors.address = "Address must be at least 5 characters.";
    }

    // Cross validation: GST and PAN matching
    if (trimmedGst && trimmedPan) {
      const panFromGst = trimmedGst.substring(2, 12);
      if (panFromGst !== trimmedPan) {
        newErrors.pan_no = "PAN Number does not match with GST Number.";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Client-side duplicate check
    const exists = (Array.isArray(divisions) ? divisions : []).some(
      (d) => d.division?.trim().toLowerCase() === trimmedDivision.toLowerCase() &&
             d.company_name?.trim().toLowerCase() === trimmedCompany.toLowerCase()
    );
    if (exists) {
      const msg = "Division with this company name already exists";
      toast.info(msg);
      setErrors({ division: msg });
      return;
    }

    setErrors({});
    try {
      await DivisionCreate({
        division: trimmedDivision,
        company_name: trimmedCompany,
        gst_no: trimmedGst || null,
        pan_no: trimmedPan || null,
        address: trimmedAddress || null,
      });
      toast.success("Division added successfully!");
      resetAddForm();
      setOpenAddModal(false);
      await refreshDivisions();
    } catch (error) {
      const serverErrors = error?.response?.data;
      if (serverErrors) {
        const newErrors = {};
        if (serverErrors.division) {
          newErrors.division = serverErrors.division[0] || serverErrors.division;
          toast.info(newErrors.division);
        }
        if (serverErrors.company_name) {
          newErrors.company_name = serverErrors.company_name[0] || serverErrors.company_name;
        }
        if (serverErrors.gst_no) {
          newErrors.gst_no = serverErrors.gst_no[0] || serverErrors.gst_no;
        }
        if (serverErrors.pan_no) {
          newErrors.pan_no = serverErrors.pan_no[0] || serverErrors.pan_no;
        }
        if (serverErrors.address) {
          newErrors.address = serverErrors.address[0] || serverErrors.address;
        }
        setErrors(newErrors);
      } else {
        toast.error("Failed to add division");
      }
    }
  };

  const handleDelete = async (division_id) => {
    try {
      await DivisionDelete(division_id);
      toast.info("Division deleted.");
      await refreshDivisions();
    } catch (error) {
      toast.error("Failed to delete division");
    }
  };

  // Mobile Card Component
  const MobileDivisionCard = ({ division, index }) => {
    const isExpanded = expandedDivision === division.division_id;

    return (
      <Box key={division.division_id}>
        {/* Main Row */}
        <Box
          onClick={() =>
            setExpandedDivision(
              expandedDivision === division.division_id ? null : division.division_id
            )
          }
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.5,
            cursor: "pointer",
            bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
            "&:active": {
              bgcolor: "#e3f2fd",
            },
            borderLeft: "4px solid #1976d2",
          }}
        >
          <Box sx={{ flex: 1 }}>
            {/* Division Name */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography sx={{ fontSize: "0.7rem", color: "#666", minWidth: 25 }}>
                #{index + 1}
              </Typography>
              <Typography sx={{ fontWeight: "bold", fontSize: "0.85rem", flex: 1 }}>
                {division.division}
              </Typography>
            </Box>

            {/* Company Name */}
            {division.company_name && (
              <Typography sx={{ fontSize: "0.7rem", color: "#666", mb: 0.3 }}>
                Company: {division.company_name}
              </Typography>
            )}

            {/* GST & PAN in same row */}
            <Box sx={{ display: "flex", gap: 2, mb: 0.3 }}>
              {division.gst_no && (
                <Typography sx={{ fontSize: "0.65rem", color: "#666" }}>
                  GST: {division.gst_no}
                </Typography>
              )}
              {division.pan_no && (
                <Typography sx={{ fontSize: "0.65rem", color: "#666" }}>
                  PAN: {division.pan_no}
                </Typography>
              )}
            </Box>

            {/* Date */}
            <Typography sx={{ fontSize: "0.7rem", color: "#666" }}>
              {formatOnlyDate(division.created_at)}
            </Typography>
          </Box>

          {/* Expand Icon */}
          <IconButton size="small" sx={{ ml: 1 }}>
            {isExpanded ? (
              <ExpandLess fontSize="small" />
            ) : (
              <ExpandMore fontSize="small" />
            )}
          </IconButton>
        </Box>

        {/* Expanded Details */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
              borderLeft: "4px solid #1976d2",
            }}
          >
            {/* Division Name */}
            <Box sx={{ mb: 0.8 }}>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  color: "#999",
                  letterSpacing: "0.5px",
                  mb: 0.3,
                }}
              >
                DIVISION NAME
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 500, color: "#555" }}>
                {division.division}
              </Typography>
            </Box>

            {/* Company Name */}
            <Box sx={{ mb: 0.8 }}>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  color: "#999",
                  letterSpacing: "0.5px",
                  mb: 0.3,
                }}
              >
                COMPANY NAME
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 500, color: "#555" }}>
                {division.company_name || "N/A"}
              </Typography>
            </Box>

            {/* GST Number */}
            <Box sx={{ mb: 0.8 }}>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  color: "#999",
                  letterSpacing: "0.5px",
                  mb: 0.3,
                }}
              >
                GST NUMBER
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                {division.gst_no || "N/A"}
              </Typography>
            </Box>

            {/* PAN Number */}
            <Box sx={{ mb: 0.8 }}>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  color: "#999",
                  letterSpacing: "0.5px",
                  mb: 0.3,
                }}
              >
                PAN NUMBER
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                {division.pan_no || "N/A"}
              </Typography>
            </Box>

            {/* Address */}
            <Box sx={{ mb: 0.8 }}>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  color: "#999",
                  letterSpacing: "0.5px",
                  mb: 0.3,
                }}
              >
                ADDRESS
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                {division.address || "N/A"}
              </Typography>
            </Box>

            {/* Created Date */}
            <Box sx={{ mb: 0.8 }}>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  color: "#999",
                  letterSpacing: "0.5px",
                  mb: 0.3,
                }}
              >
                CREATED DATE
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                {formatOnlyDate(division.created_at)}
              </Typography>
            </Box>

            <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 0.6, justifyContent: "flex-end" }}>
              {/* Edit */}
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(division);
                }}
                sx={{
                  bgcolor: "#e8f5e9",
                  borderRadius: "6px",
                  width: 32,
                  height: 32,
                  "&:hover": {
                    bgcolor: "#c8e6c9",
                    transform: "scale(1.05)",
                  },
                  transition: "all 0.2s",
                }}
              >
                <EditIcon sx={{ fontSize: "1rem", color: "#4caf50" }} />
              </IconButton>

              {/* Delete */}
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setAnchorEl(e.currentTarget);
                  setDivisionToDelete(division.division_id);
                }}
                sx={{
                  bgcolor: "#ffebee",
                  borderRadius: "6px",
                  width: 32,
                  height: 32,
                  "&:hover": {
                    bgcolor: "#ffcdd2",
                    transform: "scale(1.05)",
                  },
                  transition: "all 0.2s",
                }}
              >
                <DeleteIcon sx={{ fontSize: "1rem", color: "#f44336" }} />
              </IconButton>
            </Box>
          </Box>
        </Collapse>

        <Divider />
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 0 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: -3,
          pt: -2,
          mb: 1,
        }}
      >
        <TextField
          label="Search by division, company, GST, PAN, Address"
          variant="outlined"
          size="small"
          fullWidth
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{
            mr: 2,
            "& .MuiInputBase-root": {
              height: 30,
              borderRadius: 0,
            },
            "& .MuiInputLabel-root": {
              fontSize: "0.7rem",
            },
          }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            resetAddForm();
            setOpenAddModal(true);
          }}
          sx={{ height: 30, borderRadius: 0, fontSize: "0.65rem" }}
        >
          <AddIcon fontSize="small" sx={{ mr: 1 }} /> Division
        </Button>
      </Box>

      {/* Conditional Rendering */}
      <Box id="project-table-print">
        {isMobile ? (
          // Mobile View
          <Box sx={{ bgcolor: "background.paper", pb: 2, maxHeight: "70vh", overflow: "auto" }}>
            {getFilteredSortedDivisions().map((division, index) => (
              <MobileDivisionCard
                key={division.division_id}
                division={division}
                index={index}
              />
            ))}
          </Box>
        ) : (
          // Desktop Table View
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: 400,
              overflowX: "auto",
              border: "1px solid #e0e0e0",
              borderRadius: 1,
            }}
          >
            <Table
              size="small"
              stickyHeader
              sx={{
                minWidth: 1200,
                "& td, & th": {
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  borderBottom: "1px solid #ccc",
                },
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#f5f5f5",
                    "& th": {
                      fontWeight: "bold",
                      color: "#333",
                      borderBottom: "1px solid #999",
                      backgroundColor: "#f5f5f5",
                      fontSize: "0.8rem",
                    },
                  }}
                >
                  <TableCell>S. No.</TableCell>
                  <TableCell>Division</TableCell>
                  <TableCell>Company Name</TableCell>
                  <TableCell>GST No.</TableCell>
                  <TableCell>PAN No.</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell className="no-print">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredSortedDivisions().map((division, index) => (
                  <TableRow
                    hover
                    key={division.division_id}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                      "& td": {
                        padding: "4px 8px",
                        fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 150,
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? "black" : "inherit",
                        color: (theme) =>
                          theme.palette.mode === "dark" ? "white" : "inherit",
                      },
                      "&:hover": {
                        backgroundColor: "#e3f2fd !important",
                      },
                    }}
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{division.division}</TableCell>
                    <TableCell>{division.company_name || "-"}</TableCell>
                    <TableCell>{division.gst_no || "-"}</TableCell>
                    <TableCell>{division.pan_no || "-"}</TableCell>
                    <TableCell
                      title={division.address || ""}
                      sx={{ maxWidth: 200 }}
                    >
                      {division.address || "-"}
                    </TableCell>
                    <TableCell>{formatOnlyDate(division.created_at)}</TableCell>
                    <TableCell>
                      <IconButton
                        sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                        onClick={() => handleEdit(division)}
                        color="primary"
                        size="small"
                      >
                        <EditIcon transform="scale(0.6)" />
                      </IconButton>
                      <IconButton
                        sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                        onClick={(e) => {
                          setAnchorEl(e.currentTarget);
                          setDivisionToDelete(division.division_id);
                        }}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon transform="scale(0.6)" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Edit Modal */}
      <Dialog
        open={openEditModal}
        onClose={() => {
          setOpenEditModal(false);
          resetEditForm();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Division</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Division Name"
                value={editDivisionName}
                onChange={(e) => {
                  setEditDivisionName(e.target.value);
                  if (e.target.value.trim() !== "") {
                    setErrors((prev) => ({ ...prev, division: undefined }));
                  }
                }}
                fullWidth
                required
                error={Boolean(errors.division)}
                helperText={errors.division}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Company Name"
                value={editCompanyName}
                onChange={(e) => {
                  setEditCompanyName(e.target.value);
                  if (e.target.value.trim() !== "") {
                    setErrors((prev) => ({ ...prev, company_name: undefined }));
                  }
                }}
                fullWidth
                required
                error={Boolean(errors.company_name)}
                helperText={errors.company_name}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="GST Number"
                value={editGstNo}
                onChange={(e) => {
                  setEditGstNo(e.target.value.toUpperCase());
                  setErrors((prev) => ({ ...prev, gst_no: undefined }));
                }}
                fullWidth
                error={Boolean(errors.gst_no)}
                helperText={errors.gst_no || "Format: 22AAAAA0000A1Z5"}
                size="small"
                inputProps={{ maxLength: 15 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="PAN Number"
                value={editPanNo}
                onChange={(e) => {
                  setEditPanNo(e.target.value.toUpperCase());
                  setErrors((prev) => ({ ...prev, pan_no: undefined }));
                }}
                fullWidth
                error={Boolean(errors.pan_no)}
                helperText={errors.pan_no || "Format: ABCDE1234F"}
                size="small"
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                value={editAddress}
                onChange={(e) => {
                  setEditAddress(e.target.value);
                  setErrors((prev) => ({ ...prev, address: undefined }));
                }}
                fullWidth
                multiline
                rows={3}
                error={Boolean(errors.address)}
                helperText={errors.address}
                size="small"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenEditModal(false);
              resetEditForm();
            }}
            color="error"
          >
            Close
          </Button>
          <Button onClick={handleUpdateDivision} color="primary" variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Modal */}
      <Dialog
        open={openAddModal}
        onClose={() => {
          setOpenAddModal(false);
          resetAddForm();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add New Division</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Division Name"
                value={newDivisionName}
                onChange={(e) => {
                  setNewDivisionName(e.target.value);
                  if (e.target.value.trim() !== "") {
                    setErrors((prev) => ({ ...prev, division: undefined }));
                  }
                }}
                fullWidth
                required
                error={Boolean(errors.division)}
                helperText={errors.division}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Company Name"
                value={newCompanyName}
                onChange={(e) => {
                  setNewCompanyName(e.target.value);
                  if (e.target.value.trim() !== "") {
                    setErrors((prev) => ({ ...prev, company_name: undefined }));
                  }
                }}
                fullWidth
                required
                error={Boolean(errors.company_name)}
                helperText={errors.company_name}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="GST Number"
                value={newGstNo}
                onChange={(e) => {
                  setNewGstNo(e.target.value.toUpperCase());
                  setErrors((prev) => ({ ...prev, gst_no: undefined }));
                }}
                fullWidth
                error={Boolean(errors.gst_no)}
                helperText={errors.gst_no || "Format: 22AAAAA0000A1Z5"}
                size="small"
                inputProps={{ maxLength: 15 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="PAN Number"
                value={newPanNo}
                onChange={(e) => {
                  setNewPanNo(e.target.value.toUpperCase());
                  setErrors((prev) => ({ ...prev, pan_no: undefined }));
                }}
                fullWidth
                error={Boolean(errors.pan_no)}
                helperText={errors.pan_no || "Format: ABCDE1234F"}
                size="small"
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                value={newAddress}
                onChange={(e) => {
                  setNewAddress(e.target.value);
                  setErrors((prev) => ({ ...prev, address: undefined }));
                }}
                fullWidth
                multiline
                rows={3}
                error={Boolean(errors.address)}
                helperText={errors.address}
                size="small"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenAddModal(false);
              resetAddForm();
            }}
            color="error"
          >
            Close
          </Button>
          <Button onClick={handleAddNewDivision} color="primary" variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setDivisionToDelete(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body2">Are you sure?</Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
            <Button
              color="error"
              size="small"
              onClick={() => {
                handleDelete(divisionToDelete);
                setAnchorEl(null);
                setDivisionToDelete(null);
              }}
            >
              Yes
            </Button>
            <Button
              size="small"
              onClick={() => {
                setAnchorEl(null);
                setDivisionToDelete(null);
              }}
            >
              No
            </Button>
          </Box>
        </Box>
      </Popover>

      <ToastContainer position="top-right" autoClose={3000} />
    </Box>
  );
};

export default DivisionManagementTable;